<template>
    <div class="organization-dashboard-tab">
        <GuideVideoBanner 
            v-if="guideData && guideData.content && showGuideCard"
            :text="guideData.content.text"
            :subtext="guideData.content.subText"
            :videoUrl="guideData.content.videoUrl"
            :videoThumbnail="guideData.content.videoThumbnail"
            @close="handleGuideClose"
        />
        <OrganizationDashboardSection @add="handleTileAdd" @draft="handleTileDraft" />
        <NeedsAttentionCard @action="handleNeedsAttentionAction" />
        <div class="organization-dashboard-bottom-row">
            <RecentActivity class="organization-dashboard-bottom-row__activity" />
            <OrganizationHealthCard class="organization-dashboard-bottom-row__health" />
        </div>

        <!-- Add Position Modal -->
        <AddPositionDialog
            :show="showAddPositionModal"
            OrgObjectType="Position"
            ghostText="+ Add"
            description="You are about to add a new"
            type="add"
            :button-label="isLoading ? 'Saving' : 'Save'"
            @onAdd="handleAddPosition"
            @close="showAddPositionModal = false"
            :allowSpecialCharacters="true"
        />

        <!-- Add Role Modal -->
        <AddRoleDialog
            :value="showAddRoleModal"
            @input="showAddRoleModal = $event"
            type="add"
            @onSave="handleAddRole"
            @clearError="clearRoleError"
            :errorMessage="roleErrorMessage"
            :existingNames="[]"
        />

        <!-- Add Responsibility Modal -->
        <AddResponsibility
            :value="showAddResponsibilityModal"
            @input="showAddResponsibilityModal = $event"
            @onSave="handleAddResponsibility($event)"
            @clearError="clearResponsibilityError"
            :errorMessage="responsibilityErrorMessage"
        />

        <!-- Add Employee Modal -->
        <AddEmployeeDialog
            :show="showAddEmployeeModal"
            @onSave="handleAddEmployee"
            @close="showAddEmployeeModal = false"
        />

        <!-- Add Department Modal (ER-12265: Departments tile "+ Add" opens the
             same dialog as the Departments tab, with the same duplicate-name
             validation and "Save as Draft" semantics.) -->
        <AddDepartmentDialog
            :show="showAddDepartmentModal"
            :errorMessage="departmentErrorMessage"
            @onAdd="handleAddDepartmentSubmit"
            @close="showAddDepartmentModal = false"
            @clearError="clearDepartmentError"
        />
    </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import GuideVideoBanner from '@components/GuideVideoBanner/GuideVideoBanner.vue';
import OrganizationDashboardSection from '@components/organizations/dashboard/OrganizationDashboardSection.vue';
import NeedsAttentionCard from '@components/organizations/dashboard/NeedsAttentionCard/NeedsAttentionCard.vue';
import RecentActivity from '@components/RecentActivity/RecentActivity.vue';
import OrganizationHealthCard from '@components/OrganizationHealthCard/OrganizationHealthCard.vue';
import AddPositionDialog from '@components/AddPositionDialog/AddPositionDialog.vue';
import AddEmployeeDialog from '@components/organizations/employees/AddEmployeeDialog/AddEmployeeDialog.vue';
import AddResponsibility from '@components/RolesCatalog/AddResponsibilityDialog/AddResponsibilityDialog.vue';
import AddRoleDialog from '@components/RolesCatalog/AddRoleDialog/AddRoleDialog.vue';
import AddDepartmentDialog from '@components/AddDepartmentDialog/AddDepartmentDialog.vue';
import { useSidebarCountStore } from '@/store/pinia/sidebarCount';
import { useFilters } from '@/composables/filters/useFilters';
import {
    createPosition,
    addRole,
    editRole,
    createResponsibility,
    updateResponsibilityContext,
    createEmployee,
    addDepartment,
} from '@/api';
import { getResponsibilityCreateErrorMessage } from '@components/utils/responsibilityNameError';
import { getApiErrorDetail } from '@components/utils/apiErrorDetail';
import { refreshAccountCounts } from '@/utils/accountCountUtils';

const app = getCurrentInstance();
const { proxy } = app;

const props = defineProps({
    guideData: {
        type: Object,
        default: null
    }
});

const sidebarCountStore = useSidebarCountStore();

const emit = defineEmits(['guide-closed', 'change-tab', 'drilldown']);

const showGuideCard = ref(true);

// Modal states
const showAddPositionModal = ref(false);
const showAddRoleModal = ref(false);
const showAddResponsibilityModal = ref(false);
const showAddEmployeeModal = ref(false);
const showAddDepartmentModal = ref(false);
const isLoading = ref(false);
const responsibilityErrorMessage = ref('');
const roleErrorMessage = ref('');
const departmentErrorMessage = ref('');

// Guide data is provided by the parent (OrganizationPage), which resolves it
// through the shared useKnockGuides singleton.
const guideData = computed(() => props.guideData);

const handleGuideClose = async () => {
    showGuideCard.value = false;
    
    if (guideData.value && guideData.value.markAsArchived) {
        try {
            await guideData.value.markAsArchived();
        } catch (error) {
            console.error('Error archiving guide in Knock:', error);
        }
    }
    
    emit('guide-closed');
};

const handleTabChange = (tabName) => {
    emit('change-tab', tabName);
};

// ER-12265 — a tile's "+ Add" opens the same creation dialog as the object's own
// tab. The object id comes straight from OrganizationDashboardSection.metrics.
const handleTileAdd = (objectId) => {
    if (objectId === 'employees') {
        showAddEmployeeModal.value = true;
    } else if (objectId === 'positions') {
        showAddPositionModal.value = true;
    } else if (objectId === 'roles') {
        showAddRoleModal.value = true;
    } else if (objectId === 'responsibilities') {
        showAddResponsibilityModal.value = true;
    } else if (objectId === 'departments') {
        showAddDepartmentModal.value = true;
    }
};

// ER-12265 — a tile's "n in Draft" link opens that object's list filtered to Draft
// only, by persisting the same filter key/value each object's own tab already
// consumes (filterDefaultsRegistry) and switching to that tab. Positions persists
// its status chips capitalized ("Draft"); the other four persist lowercase "draft".
const TILE_DRAFT_FILTERS = {
    employees: { key: 'employeesOrganizationTable', filters: ['draft'] },
    positions: { key: 'organizationPositions', filters: ['Draft'] },
    roles: { key: 'readinessRolesData', filters: ['draft'] },
    responsibilities: { key: 'organizationResponsibilities', filters: ['draft'] },
    departments: { key: 'organizationDepartmentsData', filters: ['draft'] },
};

const TILE_TAB_LABELS = {
    employees: 'Employees',
    positions: 'Positions',
    roles: 'Roles',
    responsibilities: 'Responsibilities',
    departments: 'Departments',
};

const handleTileDraft = (objectId) => {
    const spec = TILE_DRAFT_FILTERS[objectId];
    const tabName = TILE_TAB_LABELS[objectId];
    if (!spec || !tabName) {
        return;
    }
    try {
        useFilters().setFilters({ key: spec.key, filters: [...spec.filters] });
    } catch (error) {
        console.error(`Failed to persist ${objectId} Draft filter:`, error);
    }
    emit('change-tab', tabName);
};

// ER-12269: the Needs attention row action opens the matching filtered list. The
// Employees-without-a-Position count (ER-12267) is `occupies=false`, which the list
// exposes as the stable "does_not_occupy" filter value (employeeFilterPayload.js) plus
// the `active` status. Reuse the persisted filter store (useFilters -> loadFiltersOnReload
// on the Employees table mount) instead of inventing a new navigation param.
const handleNeedsAttentionAction = (payload) => {
    if (!payload || payload.tab !== 'employees') {
        return;
    }
    const filters = Array.isArray(payload.filter) ? payload.filter : ['active', 'does_not_occupy'];
    try {
        useFilters().setFilters({
            key: 'employeesOrganizationTable',
            filters: [...filters],
        });
    } catch (error) {
        console.error('Failed to persist Needs attention filter:', error);
    }
    emit('change-tab', 'Employees');
};

const handleAddPosition = async (name) => {
    try {
        isLoading.value = true;
        proxy.$loader.setAppLoading(true);
        const accountID = localStorage.getItem('currentAccountID');
        
        let { data } = await createPosition(accountID, { name });

        if (data?.id) {
            showAddPositionModal.value = false;
            proxy.$loader.setAppLoading(false);
            // ER-12265: stay on the dashboard and refresh the tile counts instead of
            // navigating to the new position's detail page.
            await refreshAccountCounts(accountID);
            return;
        }
        
        showAddPositionModal.value = false;
    } catch (error) {
        console.error('Error creating position:', error);
    } finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};

const handleAddRole = async ({ name, context = '' } = {}) => {
    try {
        isLoading.value = true;
        roleErrorMessage.value = "";
        proxy.$loader.setAppLoading(true);
        const accountID = localStorage.getItem('currentAccountID');
        
        let { data } = await addRole(accountID, { name });
        
        if (data?.id) {
            if (context) {
                await editRole(accountID, data.id, { context });
            }
            showAddRoleModal.value = false;
            proxy.$loader.setAppLoading(false);
            // ER-12265: stay on the dashboard and refresh the tile counts instead of
            // navigating to the new role's detail page.
            await refreshAccountCounts(accountID);
            return;
        }
        
        showAddRoleModal.value = false;
    } catch (error) {
        console.error('Error creating role:', error);
        let errorMessage = error.response?.data?.[0] || error.response?.data?.name?.[0];
        if (errorMessage === "Same name already exists" || errorMessage?.includes("already exists")) {
            roleErrorMessage.value = "Name already exists. Only unique names are allowed.";
        } else {
            roleErrorMessage.value = errorMessage || "Failed to create role.";
        }
    }
    finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};

const clearResponsibilityError = () => {
    responsibilityErrorMessage.value = '';
};

const clearRoleError = () => {
    roleErrorMessage.value = '';
};

const clearDepartmentError = () => {
    departmentErrorMessage.value = '';
};

const handleAddResponsibility = async ({ name: responsibilityName, context: responsibilityContext = '' } = {}) => {
    try {
        isLoading.value = true;
        proxy.$loader.setAppLoading(true);
        const accountID = localStorage.getItem('currentAccountID');
        
        // Call API directly to properly catch errors
        const payload = {
            name: responsibilityName,
            account: accountID,
        };
        const { data: result } = await createResponsibility(accountID, payload);
        
        if (result?.id) {
            if (responsibilityContext) {
                await updateResponsibilityContext(accountID, result.id, { context: responsibilityContext });
            }
            showAddResponsibilityModal.value = false;
            proxy.$loader.setAppLoading(false);
            // ER-12265: stay on the dashboard and refresh the tile counts instead of
            // navigating to the new responsibility's detail page.
            await refreshAccountCounts(accountID);
            return;
        }
        
        showAddResponsibilityModal.value = false;
    } catch (error) {
        console.error('Error creating responsibility:', error);
        // Keep dialog open and show error message in the input component
        responsibilityErrorMessage.value = getResponsibilityCreateErrorMessage(error);
        // Dialog stays open (we don't set showAddResponsibilityModal.value = false here)
    } finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};

const handleAddEmployee = async (payload) => {
    try {
        isLoading.value = true;
        proxy.$loader.setAppLoading(true);
        const accountID = localStorage.getItem('currentAccountID');
        
        const response = await createEmployee(accountID, payload);
        
        // Check if this is a new employee (no id in payload)
        if (!payload?.id && response?.data?.id) {
            showAddEmployeeModal.value = false;
            proxy.$loader.setAppLoading(false);
            // ER-12265: stay on the dashboard and refresh the tile counts instead of
            // navigating to the new employee's detail page.
            await refreshAccountCounts(accountID);
            return;
        }
        
        showAddEmployeeModal.value = false;
    } catch (error) {
        console.error('Error creating employee:', error);
    } finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};

// ER-12265 — Departments tile "+ Add" mirrors OrganizationDepartmentsTable's add
// flow: same AddDepartmentDialog, same "Save as Draft" (a new Department is a Draft),
// and the same duplicate-name validation message.
const handleAddDepartmentSubmit = async (name) => {
    const departmentName = typeof name === "string" ? name.trim() : "";
    if (!departmentName) return;

    try {
        isLoading.value = true;
        proxy.$loader.setAppLoading(true);
        clearDepartmentError();
        const accountID = localStorage.getItem('currentAccountID');
        const { data } = await addDepartment(accountID, { name: departmentName });

        if (data?.id) {
            showAddDepartmentModal.value = false;
            proxy.$loader.setAppLoading(false);
            await refreshAccountCounts(accountID);
        }
    } catch (error) {
        console.error('Error creating department:', error);
        const apiDetail = getApiErrorDetail(error);
        const apiError = error.response?.data?.[0] || error.response?.data?.name?.[0];
        if (apiDetail) {
            departmentErrorMessage.value = apiDetail;
        } else if (apiError === 'Same name already exists' || apiError?.includes?.('already exists')) {
            departmentErrorMessage.value = 'Name already exists. Only unique names are allowed.';
        } else {
            departmentErrorMessage.value = apiError || 'An error occurred while creating the department.';
        }
    } finally {
        isLoading.value = false;
        proxy.$loader.setAppLoading(false);
    }
};
</script>

<style scoped>
.organization-dashboard-tab { 
    display: flex;
    flex-direction: column;
    gap: 15px;
}

/* Rule 11: Recent activity (left, wider) and Organization health (right, narrower)
   side by side at ~59% / 41% of the row (Figma: 634.2px / 436.8px of 1086px, 15px gap).
   The split is set on the outer (border-box) widths: a flex-grow ratio from a 0 basis would
   only divide the content boxes and the 42px padding + border per card would skew it.
   Both cards still grow, so a hidden card leaves no gap and the other fills the row. */
.organization-dashboard-bottom-row {
    display: flex;
    align-items: flex-start;
    gap: 15px;
}

.organization-dashboard-bottom-row:empty {
    display: none;
}

.organization-dashboard-bottom-row__activity {
    box-sizing: border-box;
    flex: 1 1 calc((100% - 15px) * 1.452 / 2.452);
    min-width: 0;
}

.organization-dashboard-bottom-row__health {
    box-sizing: border-box;
    flex: 1 1 calc((100% - 15px) / 2.452);
    min-width: 280px;
}

.guide-video-banner {
    margin-top: 0px;
}
</style>